import { useMemo, useState } from "react";
import { IInvoice } from "../../../../create/interfaces/invoice.interfaces";
import { useGetScopeOfWork } from "../../../../../scope-of-work/hooks/useGetScopeOfWork";

interface useDetailsViewProps {
  scopeOfWorkUid?: string | null;
  invoice: IInvoice;
}

export const useDetailsView = ({ scopeOfWorkUid, invoice }: useDetailsViewProps) => {
  const { data: scopeOfWork, isLoading: scopeOfWorkLoading } = useGetScopeOfWork({ 
    scopeOfWorkUid: scopeOfWorkUid || undefined 
  });

  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const isReady = !!scopeOfWork && !scopeOfWorkLoading;

  const toggleRow = (rowName: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(rowName) ? next.delete(rowName) : next.add(rowName);
      return next;
    });
  };

  const { mainAreas, otherAreas } = useMemo(() => {
    if (!isReady) return { mainAreas: [], otherAreas: [] };

    return scopeOfWork.areasByScopesOfWork.reduce(
      (acc, area) => {
        if (area.otherArea === true) acc.otherAreas.push(area);
        else acc.mainAreas.push(area);
        return acc;
      },
      { mainAreas: [] as typeof scopeOfWork.areasByScopesOfWork, otherAreas: [] as typeof scopeOfWork.areasByScopesOfWork },
    );
  }, [isReady, scopeOfWork]);

  const additionalObjects = useMemo(() => {
    if (!isReady) return [];
    return scopeOfWork.additionalObjects ?? [];
  }, [isReady, scopeOfWork]);

  const specialRequests = useMemo(() => {
    if (!isReady) return [];
    return scopeOfWork.specialRequests ?? [];
  }, [isReady, scopeOfWork]);

  const invoiceCosts = useMemo(() => {
    if (!isReady) return {};
    return {
      mainCost: invoice?.mainAreasCost,
      otherCost: invoice?.otherAreasCost,
      additionalCost: invoice?.additionalObjectsCost,
      specialCost: invoice?.specialRequestsCost,
    };
  }, [isReady, invoice]);

  const hasMainAreasData = useMemo(() => {
    return mainAreas.length > 0 || (invoice?.mainAreasCost?.total?.value ?? 0) > 0 || (invoice?.mainAreasCost?.price?.value ?? 0) > 0;
  }, [mainAreas, invoice]);

  const hasOtherAreasData = useMemo(() => {
    return otherAreas.length > 0 || (invoice?.otherAreasCost?.total?.value ?? 0) > 0 || (invoice?.otherAreasCost?.price?.value ?? 0) > 0;
  }, [otherAreas, invoice]);

  const hasAdditionalObjectsData = useMemo(() => {
    return additionalObjects.length > 0 || (invoice?.additionalObjectsCost?.total?.value ?? 0) > 0 || (invoice?.additionalObjectsCost?.price?.value ?? 0) > 0;
  }, [additionalObjects, invoice]);

  const hasSpecialRequestsData = useMemo(() => {
    return specialRequests.length > 0 || (invoice?.specialRequestsCost?.total?.value ?? 0) > 0 || (invoice?.specialRequestsCost?.price?.value ?? 0) > 0;
  }, [specialRequests, invoice]);

  const isRowExpanded = (rowName: string) => expandedRows.has(rowName);

  const expandAllRows = () => {
    setExpandedRows(new Set(["mainAreas", "otherAreas", "additionalObjects", "specialRequests", "frequencyIncrease"]));
  };

  const collapseAllRows = () => setExpandedRows(new Set());

  return {
    // readiness + raw data (optional but usually helpful for the UI)
    isReady,
    scopeOfWork,
    scopeOfWorkLoading,

    // always-safe derived data (never undefined)
    mainAreas,
    otherAreas,
    additionalObjects,
    specialRequests,
    invoiceCosts,

    // data visibility flags
    hasMainAreasData,
    hasOtherAreasData,
    hasAdditionalObjectsData,
    hasSpecialRequestsData,

    // UI helpers
    expandedRows,
    toggleRow,
    isRowExpanded,
    expandAllRows,
    collapseAllRows,
  };
};
